import React from 'react';
import qs from 'qs';
import { withRouter } from 'react-router';
import { useSelector } from 'react-redux';
import { selectors as profileSelectors } from '@src/containers/ProfileVenue';
import { hooks as filtersHooks } from '@src/containers/Filters';
import { hooks as modalsHooks } from '@src/containers/InfoModals';
import VenueRoomDetails from '@src/components/VenueRoomDetails';
import RoomCountText from '@src/components/RoomCountText';
import AvailDatePicker from '@src/components/AvailDatePicker';
import { reachGoalYM } from '@old/utils/analytics/reachGoal';
import RoomsList from '..';
import RoomsListCS from '../cleanState';

const RoomsListCDefault = withRouter(({ history, location }) => {
	const rooms = useSelector(profileSelectors.profileRoomsMapped);
	const { setFilterValues } = filtersHooks.useActions();
	const { openPanoramaModal } = modalsHooks.useActions();

	const separatedRooms = React.useMemo(() => {
		const suitRooms = [];
		const nonSuitRooms = [];
		let isWithFilter = false;

		const sorter = (a, b) => {
			if (a.position > b.position) {
				return 1;
			}
			if (a.position < b.position) {
				return -1;
			}
			return 0;
		};

		rooms.forEach((room) => {
			if (room.isWithFilter) {
				isWithFilter = true;
			}

			if (room.isSuit) {
				suitRooms.push(room);
			} else {
				nonSuitRooms.push(room);
			}
		});

		suitRooms.sort(sorter);
		nonSuitRooms.sort(sorter);

		return { suitRooms, nonSuitRooms, isWithFilter };
	}, [rooms]);

	const { suitRooms, nonSuitRooms, isWithFilter } = separatedRooms;

	const handleChangeDate = React.useCallback(
		(date) => {
			setFilterValues({
				values: { date: date ? date.format('YYYY-MM-DD') : undefined },
			});
		},
		[setFilterValues],
	);

	const DatePicker = React.useCallback(
		(props) => {
			const checkDates = () => reachGoalYM('microgoal_card_workload');
			return (
				<AvailDatePicker
					onCheckDates={checkDates}
					onChangeDate={handleChangeDate}
					{...props}
				/>
			);
		},
		[handleChangeDate],
	);

	const handlePanoramaClick = React.useCallback(
		(src) => {
			openPanoramaModal({ src });
		},
		[openPanoramaModal],
	);

	const handleClickImage = React.useCallback(
		async (imgs, index, room) => {
			const q = qs.parse(location.search, { ignoreQueryPrefix: true });
			history.push({
				search: qs.stringify(
					{ ...q, media: `${room.id}_${index}` },
					{ addQueryPrefix: true },
				),
			});
		},
		[history, location],
	);

	const title = React.useMemo(() => {
		return <RoomCountText rooms={suitRooms} />;
	}, [suitRooms]);

	const suitNodes = React.useMemo(() => {
		return suitRooms.map((room) => {
			return (
				<VenueRoomDetails
					key={room.id}
					{...room}
					onClickImage={(...args) => handleClickImage(...args, room)}
					onClickPanorama={handlePanoramaClick}
					AvailDatePicker={DatePicker}
				/>
			);
		});
	}, [suitRooms, handleClickImage, handlePanoramaClick, DatePicker]);

	const nonSuitNodes = React.useMemo(() => {
		return nonSuitRooms.map((room) => {
			return (
				<VenueRoomDetails
					key={room.id}
					{...room}
					onClickImage={(...args) => handleClickImage(...args, room)}
					onClickPanorama={handlePanoramaClick}
					AvailDatePicker={DatePicker}
				/>
			);
		});
	}, [nonSuitRooms, DatePicker, handlePanoramaClick, handleClickImage]);

	if (rooms.length === 0) {
		return <RoomsListCS id="rooms" />;
	}

	return (
		<>
			{suitRooms.length > 0 && (
				<RoomsList id="rooms" rooms={rooms} title={title}>
					{suitNodes}
				</RoomsList>
			)}
			{nonSuitRooms.length > 0 && (
				<RoomsList
					id={suitRooms.length === 0 ? 'rooms' : undefined}
					rooms={rooms}
					title={
						isWithFilter
							? t(
									'{n} пространство не подходит по параметрам|{n} пространства не подходят по параметрам|{n} пространств не подходят по параметрам',
									{ n: nonSuitRooms.length },
							  )
							: null
					}
				>
					{nonSuitNodes}
				</RoomsList>
			)}
		</>
	);
});

export default RoomsListCDefault;
